import { useMemo } from 'react';
import { isEqual } from 'lodash';
import { z } from 'zod';

import { codec, useQueryParams } from '@/hooks/router/use-query-params';

export type FilterResult = {
  filters: Record<string, string | undefined>;
  onFiltersChange: (newFilters: Record<string, string | undefined>) => void;
};

/**
 * Custom hook for managing benchmark Looker filters with URL synchronization.
 *
 * Only updates the URL if the filters have changed to prevent unnecessary re-renders.
 *
 * @example
 * ```typescript
 * // Looker filter format (value is a single string):
 * { "pack_id": "\"Pack 1\",\"Pack 2\",\"Pack 3\""" }
 *
 * // Gets converted to query params format:
 * { "pack_id": ["Pack 1", "Pack 2", "Pack 3"] }
 * ```
 *
 * @returns Object containing current filters and change handler
 */
export function useFilter(): FilterResult {
  const [queryState, queryActions] = useQueryParams({
    analysis_date_filter: {
      key: 'analysis_date',
      schema: z.string().optional(),
      codec: codec.string,
    },
    ...deviceParamsConfig,
  });

  const filters = useMemo(
    () => ({
      analysis_date_filter: queryState.analysis_date_filter,
      system_id: queryState.system_id?.join(','),
      array_id: queryState.array_id?.join(','),
      node_id: queryState.node_id?.join(','),
      container_id: queryState.container_id?.join(','),
      bank_id: queryState.bank_id?.join(','),
      pack_id: queryState.pack_id?.join(','),
      module_id: queryState.module_id?.join(','),
    }),
    [queryState],
  );

  function handleFiltersChange(newFilters: Record<string, string | undefined>) {
    if (!isEqual(filters, newFilters)) {
      queryActions.replace(convertLookerFiltersToQueryParams(newFilters));
    }
  }

  return { filters, onFiltersChange: handleFiltersChange };
}

const deviceParamsConfig = {
  system_id: {
    key: 'system_id',
    schema: z.array(z.string()).optional(),
    codec: codec.array(codec.string, '|'),
    transform: toLookerStringArray,
  },
  array_id: {
    key: 'array_id',
    schema: z.array(z.string()).optional(),
    codec: codec.array(codec.string, '|'),
    transform: toLookerStringArray,
  },
  node_id: {
    key: 'node_id',
    schema: z.array(z.string()).optional(),
    codec: codec.array(codec.string, '|'),
    transform: toLookerStringArray,
  },
  container_id: {
    key: 'container_id',
    schema: z.array(z.string()).optional(),
    codec: codec.array(codec.string, '|'),
    transform: toLookerStringArray,
  },
  bank_id: {
    key: 'bank_id',
    schema: z.array(z.string()).optional(),
    codec: codec.array(codec.string, '|'),
    transform: toLookerStringArray,
  },
  pack_id: {
    key: 'pack_id',
    schema: z.array(z.string()).optional(),
    codec: codec.array(codec.string, '|'),
    transform: toLookerStringArray,
  },
  module_id: {
    key: 'module_id',
    schema: z.array(z.string()).optional(),
    codec: codec.array(codec.string, '|'),
    transform: toLookerStringArray,
  },
};

function toLookerStringArray(
  value: string[] | undefined,
): string[] | undefined {
  if (!value || value.length === 0) {
    return undefined;
  }

  // Look requires that values are wrapped in double quotes
  return value.map((v) => `"${v}"`);
}

function convertLookerFiltersToQueryParams(
  filters: Record<string, string | undefined>,
): Record<string, string | string[] | undefined> {
  const convertedFilters = Object.fromEntries(
    Object.entries(filters).map(([key, value]) => {
      if (!value) {
        return [key, undefined];
      }

      if (Object.keys(deviceParamsConfig).includes(key)) {
        return [key, convertLookerValueToArray(value)];
      }

      return [key, value];
    }),
  );

  return convertedFilters;
}

function convertLookerValueToArray(value: string): string[] | undefined {
  let convertedValue = value.split(',');
  if (Array.isArray(convertedValue)) {
    convertedValue = convertedValue.map((v) =>
      // Remove surrounding quotes
      v.trim().replace(/^"|"$/g, ''),
    );
  } else {
    convertedValue = [convertedValue];
  }
  return convertedValue;
}
